/**
 * Format health numbers: drop trailing ".0" (100.0 → 100) but keep real decimals (98.6).
 */
export function formatHealthNumberSmart(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const rounded = Math.round(value * 10) / 10;
  if (Number.isInteger(rounded) || Math.abs(rounded - Math.round(rounded)) < 1e-6) {
    return String(Math.round(rounded));
  }
  return rounded.toFixed(1);
}

/** Stress / strain scores as whole-number percentages */
export function formatHealthPercentScore(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const v = Math.round(Math.max(0, Math.min(100, value)));
  return `${v}%`;
}

/** Whole hours + minutes → total minutes (7h 25m → 445). */
export function hmToTotalMinutes(hours: number, minutes: number): number {
  const h = Math.max(0, Math.floor(hours) || 0);
  const m = Math.min(59, Math.max(0, Math.floor(minutes) || 0));
  return h * 60 + m;
}

/** Whole hours + minutes → decimal hours, minute-accurate (7h 25m → 7.42). */
export function hmToDecimalHours(hours: number, minutes: number): number {
  const totalMinutes = hmToTotalMinutes(hours, minutes);
  if (totalMinutes <= 0) return 0;
  // Align with sleep_logs NUMERIC(5,2) — one minute ≈ 0.0167h.
  return Math.round((totalMinutes * 100) / 60) / 100;
}

/** Resolve display/storage hours from a sleep_logs row (prefers exact duration_minutes). */
export function sleepLogDisplayHours(log: {
  duration_minutes?: number | null;
  hours_slept: number;
}): number {
  if (log.duration_minutes != null && log.duration_minutes > 0) {
    return log.duration_minutes / 60;
  }
  return normalizeSleepHours(log.hours_slept);
}

/** Convert decimal hours (e.g. 7.92) to whole hours + minutes (7h 55m). */
export function decimalHoursToHm(hours: number): { h: number; m: number } {
  if (!Number.isFinite(hours) || hours <= 0) return { h: 0, m: 0 };
  const totalMinutes = Math.round(hours * 60);
  return { h: Math.floor(totalMinutes / 60), m: totalMinutes % 60 };
}

/** Normalize stored decimal hours for DB/API (minute-accurate, 2 decimal places). */
export function normalizeSleepHours(hours: number): number {
  if (!Number.isFinite(hours) || hours <= 0) return 0;
  const totalMinutes = Math.round(hours * 60);
  return Math.round((totalMinutes * 100) / 60) / 100;
}

/** Merge manual sleep_logs with Apple Health rows (manual wins per date). */
export function mergeSleepHoursByDate(
  sleepLogs: Array<{
    sleep_date: string;
    hours_slept: number;
    duration_minutes?: number | null;
    quality_rating?: number | null;
  }>,
  healthSleep: Array<{ date: string; hours: number }>,
): Map<string, { hours: number; quality_rating: number | null }> {
  const map = new Map<string, { hours: number; quality_rating: number | null }>();
  for (const h of healthSleep) {
    if (h.hours > 0) {
      map.set(h.date, { hours: h.hours, quality_rating: null });
    }
  }
  for (const log of sleepLogs) {
    const hours = sleepLogDisplayHours(log);
    if (hours > 0) {
      map.set(log.sleep_date, {
        hours,
        quality_rating: log.quality_rating ?? null,
      });
    }
  }
  return map;
}

/** Human-readable sleep duration — prefer "7h 55m" over "7.9h". */
export function formatSleepDuration(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return "—";
  const { h, m } = decimalHoursToHm(hours);
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m} min`;
}
